import React from 'react';
import { Link } from 'react-router-dom';

import { Box, InputAdornment, Stack, Typography } from '@mui/material';

import styled from '@emotion/styled';
import { useField } from 'formik';

import {
  ChipOption,
  ChipOptionsProps,
  ChipOptionsV2,
} from '../../../../../components/ChipOptions/ChipOptionsV2';
import { CurrencySign } from '../../../../../components/CurrencySign/CurrencySign';
import { StarterIcon } from '../../../../../components/Dialogs/CollabsFilters/ToolTip';
import { followerOptionsV2 } from '../../../../../components/Dialogs/CollabsFilters/options';
import FormikNumberField from '../../../../../components/Formiks/NumberField/NumberField';
import FormikTextField from '../../../../../components/Formiks/TextField/TextField';
import { HtmlTooltip } from '../../../../../components/HtmlTooltip/HtmlTooltip';
import { PremiumTooltip } from '../../../../../components/PremiumTooltip/PremiumTooltip';
import { OfferRequirementsV2 } from '../../../../../components/V2/OfferRequirements/OfferRequirementsV2';
import { OfferCategory, OfferRequirements } from '../../../../../graphql/API';
import useSubscriptionAccess from '../../../../../hooks/useSubscriptionAccess';
import { useAppSelector } from '../../../../../redux/hooks';
import Icons from '../../../../../themes/icons';
import { OFFER_REQUIREMENTS_V2 } from '../../../../CreateUpdateProducts/constants';
import { extractSubscriptionPlan } from '../../../../SubscriptionPlans/common/helper';
import { Plan } from '../../../../SubscriptionPlans/types';
import { Method } from '../../../OfferForm';
import { AttentionError } from '../../AttentionError';
import SideBySide from '../../SideBySide';
import OfferCallToConfirm from '../../formiks/OfferCallToConfirm/OfferCallToConfirm';
import ReqRadioGroup from '../../formiks/OfferRequirement/OfferRequirement';
import {
  FormValue,
  Requirement,
  StepOneFormValue,
  StepThreeFormValue,
  StepTwoFormValue,
  isAllowCallToConfirm,
} from '../../schema';

type ChipOnChange = ChipOptionsProps['onChange'];

type MinimumFollowersProps = {
  label?: string;
  name?: string;
  defaultValue?: number;
  options: ChipOption[];
  noPremiumAccess?: boolean;
};

const MinimumFollowersOptions: React.FC<MinimumFollowersProps> = ({
  label = 'Minimum followers',
  name = 'minFollowers',
  defaultValue = 0,
  options,
  noPremiumAccess = false,
}) => {
  const [, , helper] = useField(name);

  const handleOnChange = React.useCallback<ChipOnChange>(
    (e) => {
      helper.setValue(e.value);
    },
    [helper],
  );

  return (
    <Box>
      <Box sx={{ mb: '15px', display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Typography
          sx={{
            fontSize: '15px',
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
        {/* {noPremiumAccess && (
          <PremiumTooltip showStarterIcon>
            <Box display={'flex'} alignContent={'flex-start'} gap={'8px'}>
              <StarterIcon sx={{ margin: '0', fontSize: '18px', fill: '#40E5AF' }} />
              <Typography fontSize={'15px'} sx={{ textDecoration: 'underline', fontWeight: 500 }}>
                Upgrade
              </Typography>
            </Box>
          </PremiumTooltip>
        )} */}
      </Box>

      <ChipOptionsV2
        options={options}
        name={name}
        defaultValue={String(defaultValue)}
        onChange={handleOnChange}
      />
    </Box>
  );
};

type Values = StepOneFormValue & StepTwoFormValue & StepThreeFormValue;

type Props = {
  method?: Method;
  initialValues?: Values;
  values: Values;
  handlePersistFormData?: (values: Partial<FormValue>) => void;
};

export const Form3: React.FC<Props> = ({
  method = 'insert',
  initialValues,
  values,
  handlePersistFormData,
}) => {
  const venueState = useAppSelector((state) => state.venue.value);
  const { subscription } = useAppSelector((state) => state.subscription.value);

  const [field, , helper] = useField<Requirement>('requirements');
  const [, , minFollowersHelper] = useField('minFollowers');

  const subscriptionPlan: Plan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const subscriptionAccess = useSubscriptionAccess();

  const { fOptions } = React.useMemo(
    () => ({
      fOptions: followerOptionsV2(subscriptionAccess, subscriptionPlan),
    }),
    [subscriptionAccess, subscriptionPlan],
  );

  const noPremiumAccess = React.useMemo(
    () => !subscriptionAccess?.includes('PREMIUM_ACCESS_CONTROL'),
    [subscriptionAccess],
  );

  React.useEffect(() => {
    if (handlePersistFormData) handlePersistFormData({ ...(values as FormValue) });
  }, [values]);

  React.useEffect(() => {
    if (values.requirements === 'postOnTikTokVideo') {
      minFollowersHelper.setValue(0);
    }
  }, [values]);

  const handleOfferRequirementChange = (value: string) => {
    helper.setValue(value as Requirement);
  };

  const venueIsEligibleForCallConfirmOption = React.useMemo(() => {
    const hasEligiblePlan = subscriptionPlan === 'starter' || subscriptionPlan === 'plus';

    return hasEligiblePlan && venueState?.type === OfferCategory.food;
  }, [venueState, subscriptionPlan]);

  return (
    <>
      <Box mb={5} display={'flex'} flexDirection={'column'} gap={2}>
        {OFFER_REQUIREMENTS_V2.map((requirement) => (
          <OfferRequirementsV2
            key={requirement.id}
            active={requirement.value.includes(field.value as never)}
            {...requirement}
            handleChange={() => {
              handleOfferRequirementChange(requirement.value[0]);
            }}
          />
        ))}
      </Box>

      {[
        OfferRequirements.postOnInstagramStories,
        OfferRequirements.postOnInstagramStories3x,
        OfferRequirements.postOnInstagramStories2x,
        OfferRequirements.postOnInstagramFeed,
        OfferRequirements.postOnInstagramReels,
      ].includes(field.value as OfferRequirements) && (
          <Box>
            <Box sx={{ mb: '15px', display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Typography
                sx={{
                  fontSize: '15px',
                  fontWeight: 500,
                }}
              >
                Type of post
              </Typography>
              {/* {noPremiumAccess && (
                <PremiumTooltip showStarterIcon>
                  <Box display={'flex'} alignContent={'flex-start'} gap={'8px'}>
                    <StarterIcon sx={{ margin: '0', fontSize: '18px', fill: '#40E5AF' }} />
                    <Typography
                      fontSize={'15px'}
                      sx={{ textDecoration: 'underline', fontWeight: 500 }}
                    >
                      Upgrade
                    </Typography>
                  </Box>
                </PremiumTooltip>
              )} */}
            </Box>
            <ReqRadioGroup
              aria-labelledby='radio-group-requirement-label'
              name='radio-group-requirement'
              offerValue={values.offerValue || 0}
            />
            {initialValues &&
              method === 'update' &&
              values.requirements !== initialValues.requirements && <AttentionError />}

            <Box mt={5}>
              {isAllowCallToConfirm(
                values.requirements !== 'noRequirement',
                values.category,
                subscriptionPlan,
              ) && <OfferCallToConfirm />}

              {!isAllowCallToConfirm(
                values.requirements !== 'noRequirement',
                values.category,
                subscriptionPlan,
              ) &&
                venueIsEligibleForCallConfirmOption && <OfferCallToConfirm />}

              {initialValues &&
                method === 'update' &&
                Boolean(initialValues.confirmWithCall) !== Boolean(values.confirmWithCall) && (
                  <AttentionError />
                )}
            </Box>

            <Box mt={1}>
              <MinimumFollowersOptions
                defaultValue={values.minFollowers || 0}
                options={fOptions}
                noPremiumAccess={noPremiumAccess}
              />
              {initialValues &&
                method === 'update' &&
                initialValues.minFollowers !== values.minFollowers && <AttentionError />}
            </Box>
          </Box>
        )}

      <Box mt={5}>
        {values.requirements !== 'noRequirement' && (
          <>
            <Typography
              sx={{
                fontSize: '22px',
                fontWeight: 600,
                mb: '27px',
              }}
            >
              Optional
            </Typography>
            <SideBySide ratio={6}>
              <CustomBox>
                <label htmlFor='txt-dresscode' className='offer-form-label'>
                  Dresscode
                </label>
                <FormikTextField
                  className='offer-input-field'
                  id='txt-dresscode'
                  name='dressCode'
                  placeholder='Ex. casual, elegant, sportive, etc.'
                />
              </CustomBox>

              <CustomBoxTrip>
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  gap={'10px'}
                  justifyContent={'space-between'}
                  flexWrap={'nowrap'}
                >
                  <label htmlFor='txt-tip' className='offer-form-label'>
                    Suggested tip
                  </label>
                  {noPremiumAccess && (
                    <PremiumTooltip showStarterIcon>
                    <Box display={'flex'} alignContent={'flex-start'} gap={'8px'}>
                      <StarterIcon sx={{ margin: '0', fontSize: '18px', fill: '#429C12' }} />
                      <Typography fontSize={'15px'} sx={{ textDecoration: 'underline', fontWeight: 500 }}>
                        Upgrade
                      </Typography>
                    </Box>
                  </PremiumTooltip>
                  )}
                </Box>
                <FormikNumberField
                  className='offer-input-field'
                  id='txt-tip'
                  name='suggestedTip'
                  placeholder='0'
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        {venueState?.currency && <CurrencySign currency={venueState?.currency} />}
                      </InputAdornment>
                    ),
                  }}
                  disabled={noPremiumAccess}
                />
                {initialValues &&
                  method === 'update' &&
                  Number(values.suggestedTip) !== Number(initialValues.suggestedTip) && (
                    <AttentionError />
                  )}
              </CustomBoxTrip>
            </SideBySide>
          </>
        )}
      </Box>

      {field.value === 'postOnTikTokVideo' && (
        <Box mt={5}>
          <SideBySide shouldFlexEnd>
            <CustomBox>
              <Box display={'flex'} alignItems={'center'} gap={'8px'} mb={'15px'}>
                <Typography
                  sx={{
                    fontSize: '15px',
                    fontWeight: 500,
                  }}
                >
                  Tag (optional)
                </Typography>
                <HtmlTooltip
                  arrow
                  placement='top'
                  title={
                    <React.Fragment>
                      <Typography fontSize={'13px'} p={'12px'}>
                        Add your @TikTok handle to be tagged in posts
                      </Typography>
                    </React.Fragment>
                  }
                >
                  <Icons.InfoIcon />
                </HtmlTooltip>
              </Box>
              <FormikTextField
                id='txt-offer-value'
                name='tag'
                className='offer-input-field'
                placeholder='@Tag'
              />
            </CustomBox>
          </SideBySide>
        </Box>
      )}
    </>
  );
};

const CustomBox = styled.div`
  display: flex;
  flex-direction: column;
  fieldset {
    border: 1px solid #e6e6e6;
  }
`;

const CustomBoxTrip = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  fieldset {
    border: 1px solid #e6e6e6;
  }
`;
